import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCRM } from '../../context/CRMContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';

const { FiBell, FiLogOut, FiCheck, FiInfo, FiAlertCircle, FiUser, FiChevronDown } = FiIcons;

const Header = () => {
  const { user, logout } = useAuth();
  const { systemNotifications, markNotificationAsRead, markAllNotificationsAsRead } = useCRM();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const unreadCount = systemNotifications.length;

  const getNotificationIcon = (type) => {
    switch(type) {
      case 'task_assigned': return <SafeIcon icon={FiAlertCircle} className="h-5 w-5 text-blue-500" />;
      case 'security': return <SafeIcon icon={FiAlertCircle} className="h-5 w-5 text-red-500" />;
      case 'welcome': return <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-500" />;
      default: return <SafeIcon icon={FiInfo} className="h-5 w-5 text-gray-500" />;
    }
  };

  const handleLogout = () => {
    // Close menu first
    setShowUserMenu(false);
    
    // Add a small delay to ensure the UI updates before the blocking alert appears
    setTimeout(() => {
      if (window.confirm('Do you really want to log out?')) {
        logout();
      }
    }, 50);
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 relative z-30">
      <div className="flex items-center justify-between h-16 px-6">
        <div className="flex items-center">
          <div className="ml-4">
            <h1 className="text-xl lg:text-2xl font-semibold text-gray-900">
              CRM Dashboard
            </h1>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          {/* Notifications */}
          <div className="relative">
            <button 
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full relative"
              onClick={() => setShowNotifications(!showNotifications)}
            >
              <SafeIcon icon={FiBell} className="h-6 w-6" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 h-3 w-3 bg-red-500 rounded-full ring-2 ring-white"></span>
              )}
            </button>

            {/* Notification Dropdown */}
            <AnimatePresence>
              {showNotifications && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowNotifications(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-20 overflow-hidden"
                  >
                    <div className="p-4 border-b border-gray-100 flex justify-between items-center">
                      <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
                      {unreadCount > 0 && (
                        <button 
                          onClick={markAllNotificationsAsRead}
                          className="text-xs text-primary-600 hover:text-primary-800"
                        >
                          Mark all read
                        </button>
                      )}
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {unreadCount === 0 ? (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          No new notifications
                        </div>
                      ) : (
                        systemNotifications.map(notification => (
                          <div 
                            key={notification.id} 
                            className="p-4 border-b border-gray-50 hover:bg-gray-50 transition-colors cursor-pointer"
                            onClick={() => markNotificationAsRead(notification.id)}
                          >
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0 mt-1">
                                {getNotificationIcon(notification.type)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                                <p className="text-xs text-gray-500 mt-1">{notification.message}</p>
                                <p className="text-xs text-gray-400 mt-1">
                                  {format(new Date(notification.created_at), 'MMM dd, HH:mm')}
                                </p>
                              </div>
                              <div className="h-2 w-2 bg-blue-500 rounded-full flex-shrink-0 mt-2"></div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>

          {/* User Profile Dropdown */}
          <div className="relative">
            <button 
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-3 hover:bg-gray-50 p-2 rounded-lg transition-colors focus:outline-none"
            >
              <div className="h-8 w-8 bg-primary-600 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-medium">
                  {user?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                </span>
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-gray-700">{user?.full_name}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
              </div>
              <SafeIcon icon={FiChevronDown} className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${showUserMenu ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
              {showUserMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowUserMenu(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-20 overflow-hidden"
                  >
                    <div className="py-1">
                      <Link 
                        to="/profile" 
                        className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <SafeIcon icon={FiUser} className="h-4 w-4 mr-3 text-gray-400" />
                        View Profile
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="flex w-full items-center px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors border-t border-gray-100"
                      >
                        <SafeIcon icon={FiLogOut} className="h-4 w-4 mr-3" />
                        Log Out
                      </button>
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;